import React from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';

/* ------------------------------------------------------------------ *
 * Sample showcase — a purely presentational, self-contained component
 * that demonstrates StrixScreener's output on a fixed, illustrative
 * batch of four résumés. No props, no API calls: every value below is
 * static sample data. Rendered on the landing state only.
 * ------------------------------------------------------------------ */

type Tier = 'PRIORITY REVIEW' | 'FULL REVIEW' | 'STANDARD REVIEW';
type TierColor = 'success' | 'warning' | 'default';

function tierColor(tier: Tier): TierColor {
  if (tier === 'PRIORITY REVIEW') return 'success';
  if (tier === 'FULL REVIEW') return 'warning';
  return 'default';
}

// Marks used inside comparison rows and requirement lists.
type Mark = 'y' | 'p' | 'n'; // ✓ / ~ / ✗

interface RankRow {
  rank: number;
  name: string;
  score: number;
  tier: Tier;
  oneLine: string;
}

interface ReqItem {
  mark: Mark;
  label: React.ReactNode; // the requirement name (bolded)
  note: React.ReactNode; // the trailing explanation
}

// A detail body is an ordered list of blocks so section order can vary
// per candidate while staying fully typed.
type Block =
  | { kind: 'para'; body: React.ReactNode }
  | { kind: 'list'; items: React.ReactNode[] }
  | { kind: 'quotes'; items: string[] }
  | { kind: 'reqs'; items: ReqItem[] };

interface Section {
  heading: string;
  block: Block;
}

interface Candidate {
  name: string;
  score: number;
  tier: Tier;
  verdict: React.ReactNode;
  sections: Section[];
}

/* ----------------------------- data ----------------------------- */

const SAMPLE_JD = 'Senior Network Engineer, Data-Center & SD-WAN';

const RANKING: RankRow[] = [
  {
    rank: 1,
    name: 'Marcus Reyes',
    score: 92,
    tier: 'PRIORITY REVIEW',
    oneLine: 'Multi-vendor migrations with quantified outcomes',
  },
  {
    rank: 2,
    name: 'Priya Nair',
    score: 79,
    tier: 'FULL REVIEW',
    oneLine: 'Deep Cisco; thinner on SD-WAN & multi-vendor',
  },
  {
    rank: 3,
    name: 'Jordan Blake',
    score: 44,
    tier: 'STANDARD REVIEW',
    oneLine: 'Strong presentation; limited supporting detail for this role',
  },
  {
    rank: 4,
    name: 'Devin Cole',
    score: 31,
    tier: 'STANDARD REVIEW',
    oneLine: 'Early-career; below the level this role requires',
  },
];

const PASS_ONE: { mark: Mark; text: React.ReactNode; em: string }[] = [
  {
    mark: 'n',
    text: '"Spearheaded cross-functional synergies to drive results"',
    em: '— no specifics to evaluate.',
  },
  {
    mark: 'n',
    text: '"Experience with SD-WAN"',
    em: '— a keyword with no supporting detail.',
  },
];

const PASS_TWO: { mark: Mark; text: React.ReactNode; em: string }[] = [
  {
    mark: 'y',
    text: '"FortiGate→Palo Alto migration, cut $1M in licensing"',
    em: '— specific, measurable, relevant.',
  },
  {
    mark: 'y',
    text: '"VeloCloud SD-WAN, 240 sites, MPLS spend −60%"',
    em: '— clear scope and outcome.',
  },
];

const CANDIDATES: Candidate[] = [
  {
    name: 'Marcus Reyes',
    score: 92,
    tier: 'PRIORITY REVIEW',
    verdict: (
      <>
        <b>Interview first.</b> Nearly every claim is anchored to a vendor, a
        scale, and an outcome, and the experience maps cleanly onto this role's
        requirements.
      </>
    ),
    sections: [
      {
        heading: 'What holds up',
        block: {
          kind: 'list',
          items: [
            <>
              <b>FortiGate → Palo Alto migration, 40+ sites.</b> States the
              driver (end-of-life + licensing consolidation), the scale, and the
              result: <b>~$1M/yr in licensing removed</b> and a documented
              reduction in open firewall rules. Named platforms and a dollar
              figure — verifiable in a screen, not a slogan.
            </>,
            <>
              <b>VeloCloud SD-WAN rollout to 240 branch sites.</b> Cut MPLS
              spend ~60%, held voice jitter under 150 ms. The jitter target is
              the tell — it's the kind of metric that comes from running the
              deployment.
            </>,
            <>
              <b>Automation at real scale.</b> Ansible + Python pushing config
              to ~800 Cisco/Juniper devices; change windows dropped from 6 hours
              to ~20 minutes. Concrete before/after, not "familiar with
              automation."
            </>,
          ],
        },
      },
      {
        heading: 'Match to your requirements',
        block: {
          kind: 'reqs',
          items: [
            {
              mark: 'y',
              label: 'Multi-vendor (Cisco/Juniper/Palo Alto/Fortinet)',
              note: '— all four named, with work behind each.',
            },
            {
              mark: 'y',
              label: 'SD-WAN deployment',
              note: '— 240-site VeloCloud rollout with metrics.',
            },
            {
              mark: 'y',
              label: 'Firewall migration',
              note: '— the FortiGate→Palo Alto project is a direct hit.',
            },
            {
              mark: 'y',
              label: 'Automation (Python/Ansible)',
              note: '— demonstrated at 800-device scale.',
            },
            {
              mark: 'p',
              label: 'Data-center consolidation',
              note: '— adjacent (site consolidation) but no explicit DC-exit/fabric project. Worth confirming.',
            },
          ],
        },
      },
      {
        heading: 'Specificity check',
        block: {
          kind: 'para',
          body: (
            <>
              The opening summary is generic ("results-driven network leader
              passionate about…") and carries no score on its own. Underneath it
              are specifics — vendor names, site counts, a jitter figure, a
              licensing dollar amount — and those are what's scored. Writing
              style neither helps nor hurts the result.
            </>
          ),
        },
      },
      {
        heading: 'Gaps & what to confirm',
        block: {
          kind: 'list',
          items: [
            <>
              No explicit data-center consolidation / fabric (VXLAN-EVPN, Nexus)
              project — confirm depth in interview.
            </>,
            <>
              Leadership scope is implied ("led a team") but headcount isn't
              stated — clarify if the role is a lead.
            </>,
          ],
        },
      },
      {
        heading: 'Why #1',
        block: {
          kind: 'para',
          body: (
            <>
              The only candidate who meets four of five hard requirements with{' '}
              <b>quantified, vendor-specific evidence</b>. The one gap is
              adjacent and easy to confirm. Highest-confidence match in the
              pool.
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Priya Nair',
    score: 79,
    tier: 'FULL REVIEW',
    verdict: (
      <>
        <b>Strong, but narrower than this role.</b> Deep, well-documented Cisco
        routing/switching experience. The gaps are multi-vendor breadth and
        SD-WAN ownership.
      </>
    ),
    sections: [
      {
        heading: 'What holds up',
        block: {
          kind: 'list',
          items: [
            <>
              <b>Cisco campus refresh, ~1,200 access switches</b> across 14
              buildings, phased with zero-downtime cutovers. Specific, and the
              zero-downtime claim is paired with a maintenance-window method —
              credible.
            </>,
            <>
              <b>BGP/OSPF redesign</b> that collapsed a legacy flat core into a
              summarized, multi-area design; states convergence improvement in
              seconds. Real routing depth.
            </>,
            <>
              <b>ClearPass / 802.1X</b> NAC deployment — a concrete
              security-adjacent result.
            </>,
          ],
        },
      },
      {
        heading: 'Match to your requirements',
        block: {
          kind: 'reqs',
          items: [
            {
              mark: 'p',
              label: 'Multi-vendor',
              note: '— deep Cisco; Juniper "exposure," no Palo Alto/Fortinet. Single-vendor risk.',
            },
            {
              mark: 'n',
              label: 'SD-WAN deployment',
              note: '— listed as a skill; no project, vendor, or scale behind it.',
            },
            {
              mark: 'p',
              label: 'Firewall migration',
              note: (
                <>
                  — ASA→Firepower <em>upgrade</em> mentioned, but as a bullet
                  without outcome.
                </>
              ),
            },
            {
              mark: 'y',
              label: 'Automation',
              note: '— Python scripting for config audits; modest but specific.',
            },
            {
              mark: 'y',
              label: 'Data-center',
              note: '— Nexus/VPC work in two DCs, clearly described.',
            },
          ],
        },
      },
      {
        heading: 'Specificity check',
        block: {
          kind: 'para',
          body: (
            <>
              Largely specific throughout — written like a practitioner. The one
              soft spot is the skills line ("SD-WAN, SDN, automation"), which
              lists capabilities without supporting projects. Everything
              described in detail holds up; the limitation is breadth, not
              specificity.
            </>
          ),
        },
      },
      {
        heading: 'Why #2',
        block: {
          kind: 'para',
          body: (
            <>
              A capable match for a Cisco-centric role. Ranks below Marcus
              because the JD explicitly asks for multi-vendor + SD-WAN
              ownership, and those two are thin here. If the DC/routing depth
              matters more than SD-WAN, this could move up — flagged for a full
              read, not an automatic pass.
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Jordan Blake',
    score: 44,
    tier: 'STANDARD REVIEW',
    verdict: (
      <>
        <b>Strong presentation, limited supporting detail.</b> Among the most
        polished résumés in the batch, and among the least specific once the
        general phrasing is set aside. Ranked lower here because the concrete,
        role-relevant detail is limited — not because of writing style.
      </>
    ),
    sections: [
      {
        heading: 'What the résumé states',
        block: {
          kind: 'quotes',
          items: [
            '"Spearheaded enterprise-wide network transformation leveraging best-in-class SD-WAN and next-gen security to drive operational excellence."',
            '"Owned end-to-end lifecycle of mission-critical infrastructure across a global footprint."',
            '"Recognized thought leader in cloud-first networking paradigms."',
          ],
        },
      },
      {
        heading: 'Specificity check',
        block: {
          kind: 'para',
          body: (
            <>
              Set the general phrasing aside and little measurable detail
              remains:{' '}
              <b>
                no vendor names, no site or device counts, no before/after, no
                dollar or percentage outcome.
              </b>{' '}
              "Enterprise-wide transformation" states no scope; "global
              footprint" names no region or number; "SD-WAN" appears three times
              with no deployment detail — a keyword without supporting specifics.
            </>
          ),
        },
      },
      {
        heading: 'Match to your requirements',
        block: {
          kind: 'reqs',
          items: [
            {
              mark: 'n',
              label: 'Multi-vendor',
              note: '— no platform named anywhere.',
            },
            {
              mark: 'n',
              label: 'SD-WAN deployment',
              note: '— stated, no supporting detail.',
            },
            {
              mark: 'n',
              label: 'Firewall migration',
              note: '— "next-gen security," no specifics.',
            },
            {
              mark: 'n',
              label: 'Automation',
              note: '— not described in concrete terms.',
            },
            {
              mark: 'p',
              label: 'Data-center',
              note: '— "mission-critical infrastructure," no scope.',
            },
          ],
        },
      },
      {
        heading: 'How to use this',
        block: {
          kind: 'para',
          body: (
            <>
              This is a <b>STANDARD REVIEW, not a reject.</b> The candidate may
              well have relevant experience the résumé states in general terms.
              StrixScreener never removes anyone from consideration — it flags
              where a closer look is worth your time. Suggested next step: if you
              review this one, ask for one project with a specific platform, a
              number, and an outcome. If that detail is there, re-rank
              accordingly.
            </>
          ),
        },
      },
    ],
  },
  {
    name: 'Devin Cole',
    score: 31,
    tier: 'STANDARD REVIEW',
    verdict: (
      <>
        <b>Clearly-scoped résumé, below the level this senior role requires.</b>{' '}
        States its scope directly; scored on experience level against the role,
        which is where it falls short.
      </>
    ),
    sections: [
      {
        heading: 'What holds up',
        block: {
          kind: 'list',
          items: [
            <>
              <b>2 years NOC / tier-2 support</b>, clearly scoped: ticket
              triage, first-line troubleshooting, some CCNA-level config on
              Cisco access switches. Specific, just early-career.
            </>,
            <>
              <b>CCNA held, CCNP in progress</b> — a clear trajectory.
            </>,
          ],
        },
      },
      {
        heading: 'Match to your requirements',
        block: {
          kind: 'reqs',
          items: [
            {
              mark: 'n',
              label: 'Multi-vendor / SD-WAN / migrations',
              note: '— no senior-level project work yet.',
            },
            {
              mark: 'p',
              label: 'Automation',
              note: '— some scripting in a lab/home context.',
            },
          ],
        },
      },
      {
        heading: 'Why #4',
        block: {
          kind: 'para',
          body: (
            <>
              Low match for <em>this</em> senior requirement. The résumé is
              specific and clearly scoped; it's scored on experience level
              against the role, which is where it falls short — not on writing
              style. Worth keeping in mind for a more junior opening.
            </>
          ),
        },
      },
    ],
  },
];

/* --------------------------- helpers ---------------------------- */

// Solid background color for a small square mark, pulled from theme tokens.
function markBg(mark: Mark): string {
  if (mark === 'y') return 'success.main';
  if (mark === 'p') return 'warning.main';
  return 'grey.500';
}

function markGlyph(mark: Mark): string {
  if (mark === 'y') return '✓';
  if (mark === 'p') return '~';
  return '✗';
}

function SquareMark({ mark, glyph }: { mark: Mark; glyph?: string }) {
  return (
    <Box
      component="span"
      sx={{
        flex: 'none',
        width: 20,
        height: 20,
        borderRadius: 1.5,
        display: 'grid',
        placeItems: 'center',
        fontSize: '0.7rem',
        fontWeight: 800,
        lineHeight: 1,
        color: '#fff',
        bgcolor: markBg(mark),
        mt: '1px',
      }}
    >
      {glyph ?? markGlyph(mark)}
    </Box>
  );
}

function SectionHeading({ children }: { children: React.ReactNode }) {
  return (
    <Typography
      variant="overline"
      component="h4"
      sx={{
        display: 'block',
        color: 'primary.main',
        fontWeight: 700,
        letterSpacing: '0.06em',
        lineHeight: 1.4,
        mt: 2.5,
        mb: 1,
        '&:first-of-type': { mt: 0 },
      }}
    >
      {children}
    </Typography>
  );
}

function BlockView({ block }: { block: Block }) {
  switch (block.kind) {
    case 'para':
      return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
          {block.body}
        </Typography>
      );
    case 'list':
      return (
        <Box component="ul" sx={{ m: 0, mb: 1.25, pl: 2.5 }}>
          {block.items.map((it, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              color="text.secondary"
              sx={{ mb: 0.75 }}
            >
              {it}
            </Typography>
          ))}
        </Box>
      );
    case 'quotes':
      return (
        <Box component="ul" sx={{ m: 0, mb: 1.25, pl: 2.5 }}>
          {block.items.map((q, i) => (
            <Typography
              key={i}
              component="li"
              variant="body2"
              sx={{ mb: 0.75, color: 'text.disabled', fontStyle: 'italic' }}
            >
              {q}
            </Typography>
          ))}
        </Box>
      );
    case 'reqs':
      return (
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mb: 1.25,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 0.9,
          }}
        >
          {block.items.map((r, i) => (
            <Box
              key={i}
              component="li"
              sx={{ display: 'flex', gap: 1.4, alignItems: 'flex-start' }}
            >
              <SquareMark mark={r.mark} />
              <Typography variant="body2" color="text.secondary">
                <Box component="span" sx={{ fontWeight: 600, color: 'text.primary' }}>
                  {r.label}
                </Box>{' '}
                {r.note}
              </Typography>
            </Box>
          ))}
        </Box>
      );
  }
}

// Left-border color for the verdict callout, keyed off the tier.
function verdictBorder(tier: Tier): string {
  if (tier === 'PRIORITY REVIEW') return 'success.main';
  if (tier === 'FULL REVIEW') return 'warning.main';
  return 'grey.500';
}

/* --------------------------- component -------------------------- */

export default function SampleShowcase() {
  return (
    <Box sx={{ mt: 6 }}>
      {/* Section seam / lead-in */}
      <Divider sx={{ mb: 3 }}>
        <Typography
          variant="overline"
          color="text.secondary"
          sx={{ fontWeight: 600, letterSpacing: '0.04em' }}
        >
          See how it reads a résumé ↓
        </Typography>
      </Divider>

      {/* Pitch */}
      <Box sx={{ textAlign: 'center', maxWidth: 640, mx: 'auto', mb: 4 }}>
        <Typography
          variant="h5"
          component="h2"
          fontWeight={700}
          sx={{ letterSpacing: '-0.015em', mb: 1 }}
        >
          It doesn't count keywords. It reads under them.
        </Typography>
        <Typography variant="body1" color="text.secondary">
          <Box component="span" sx={{ color: 'primary.main', fontWeight: 600 }}>
            Two passes:
          </Box>{' '}
          set aside the AI polish every résumé now has — then, against{' '}
          <em>your</em> job description, score the specific, verifiable work
          underneath.
        </Typography>
      </Box>

      {/* Two-pass strip */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', sm: '1fr 1fr' },
          gap: 2,
          mb: 4,
        }}
      >
        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}
          >
            <Box component="span" sx={{ color: 'primary.main' }}>
              Pass 1
            </Box>{' '}
            · set the phrasing aside
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5, mb: 1.5 }}>
            General statements carry no signal
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            {PASS_ONE.map((row, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <SquareMark mark={row.mark} glyph="0" />
                <Typography variant="body2" color="text.secondary">
                  {row.text}{' '}
                  <Box component="em" sx={{ color: 'text.disabled', fontStyle: 'normal' }}>
                    {row.em}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>

        <Paper variant="outlined" sx={{ p: 2.25 }}>
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: '0.06em' }}
          >
            <Box component="span" sx={{ color: 'primary.main' }}>
              Pass 2
            </Box>{' '}
            · score the specifics
          </Typography>
          <Typography variant="subtitle1" fontWeight={600} sx={{ mt: 0.5, mb: 1.5 }}>
            Substance, matched to the job
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.1 }}>
            {PASS_TWO.map((row, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.25, alignItems: 'flex-start' }}>
                <SquareMark mark={row.mark} glyph="✓" />
                <Typography variant="body2" color="text.secondary">
                  {row.text}{' '}
                  <Box component="em" sx={{ color: 'text.disabled', fontStyle: 'normal' }}>
                    {row.em}
                  </Box>
                </Typography>
              </Box>
            ))}
          </Box>
        </Paper>
      </Box>

      {/* Sample run — ranking table */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
        <Box
          sx={{
            px: 2.75,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Sample run —{' '}
            <Box component="span" sx={{ fontWeight: 500, color: 'text.secondary' }}>
              {SAMPLE_JD}
            </Box>
          </Typography>
          <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
            4 résumés · illustrative output
          </Typography>
        </Box>
        <Box sx={{ overflowX: 'auto' }}>
          <Table size="small" sx={{ minWidth: 560 }}>
            <TableHead>
              <TableRow sx={{ '& th': { bgcolor: 'grey.100' } }}>
                <TableCell sx={headCellSx}>#</TableCell>
                <TableCell sx={headCellSx}>Candidate</TableCell>
                <TableCell sx={headCellSx}>Fit</TableCell>
                <TableCell sx={headCellSx}>Recommendation</TableCell>
                <TableCell sx={headCellSx}>One-line</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {RANKING.map((r) => (
                <TableRow key={r.rank}>
                  <TableCell
                    sx={{ fontWeight: 700, color: 'primary.main', fontVariantNumeric: 'tabular-nums' }}
                  >
                    {r.rank}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>{r.name}</TableCell>
                  <TableCell sx={{ fontWeight: 600, fontVariantNumeric: 'tabular-nums' }}>
                    {r.score}
                  </TableCell>
                  <TableCell>
                    <Chip label={r.tier} color={tierColor(r.tier)} size="small" />
                  </TableCell>
                  <TableCell sx={{ color: 'text.secondary' }}>{r.oneLine}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      </Paper>

      {/* Detailed evaluations */}
      <Paper variant="outlined" sx={{ overflow: 'hidden', mb: 3 }}>
        <Box
          sx={{
            px: 2.75,
            py: 2,
            borderBottom: 1,
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.5,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle1" fontWeight={700}>
            Detailed evaluations
          </Typography>
          <Chip
            icon={<CheckIcon />}
            label="the full reasoning — click any name"
            color="success"
            variant="outlined"
            size="small"
          />
        </Box>

        {CANDIDATES.map((c, i) => (
          <Accordion
            key={c.name}
            defaultExpanded={i === 0}
            disableGutters
            elevation={0}
            square
            sx={{
              '&:before': { display: 'none' },
              borderTop: i === 0 ? 0 : 1,
              borderColor: 'divider',
            }}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ px: 2.75 }}>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1.5,
                  width: '100%',
                  flexWrap: 'wrap',
                }}
              >
                <Typography fontWeight={600}>{c.name}</Typography>
                <Box
                  sx={{
                    ml: { xs: 0, sm: 'auto' },
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1.5,
                  }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  >
                    Fit {c.score}
                  </Typography>
                  <Chip label={c.tier} color={tierColor(c.tier)} size="small" />
                </Box>
              </Box>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 2.75, pb: 3, pt: 0 }}>
              {/* Verdict callout */}
              <Box
                sx={{
                  bgcolor: 'grey.50',
                  borderLeft: 3,
                  borderColor: verdictBorder(c.tier),
                  borderRadius: '0 8px 8px 0',
                  px: 2,
                  py: 1.5,
                  mb: 2.25,
                }}
              >
                <Typography variant="body2">{c.verdict}</Typography>
              </Box>

              {c.sections.map((s, si) => (
                <React.Fragment key={si}>
                  <SectionHeading>{s.heading}</SectionHeading>
                  <BlockView block={s.block} />
                </React.Fragment>
              ))}
            </AccordionDetails>
          </Accordion>
        ))}
      </Paper>

      {/* Trust footer */}
      <Paper
        variant="outlined"
        sx={{ p: 2.5, display: 'flex', gap: 1.75, alignItems: 'flex-start' }}
      >
        <ShieldOutlinedIcon sx={{ color: 'primary.main', mt: '2px' }} />
        <Typography variant="body2" color="text.secondary">
          <Box component="span" sx={{ fontWeight: 700, color: 'text.primary' }}>
            $1.49 per résumé. A human makes every decision.
          </Box>{' '}
          StrixScreener ranks candidates and explains its reasoning — it never
          removes anyone from consideration, and it evaluates the substance of
          the experience described, independent of writing style.
        </Typography>
      </Paper>

      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: 'block', textAlign: 'center', mt: 3 }}
      >
        Sample data · illustrative output — nothing here has been charged or sent
      </Typography>
    </Box>
  );
}

const headCellSx = {
  fontSize: '0.7rem',
  fontWeight: 700,
  letterSpacing: '0.05em',
  textTransform: 'uppercase' as const,
  color: 'text.secondary',
};
